import React from "react";
import { Box, Button, Chip, Step, StepButton, StepContent, Stepper, Typography } from "@mui/material";
import { PROGRESS_LANES } from "../../common/constants";

const inProgressChipSx = {
  bgcolor: "#fef9e7",
  color: "#b7950b",
  fontWeight: 600,
  textTransform: "uppercase",
  flexShrink: 0,
};

const stepperSx = {
  "& .MuiStepIcon-root.Mui-completed": { color: "#16a085" },
  "& .MuiStepLabel-label": {
    fontSize: "12px",
    marginTop: "10px",
    textTransform: "capitalize",
    fontWeight: "500",
    fontFamily: "inherit",
  },
};

const verticalStepperSx = {
  ...stepperSx,
  "& .MuiStepLabel-label": {
    ...stepperSx["& .MuiStepLabel-label"],
    marginTop: 0,
  },
};

export default function ProgressStepper(props) {
  const {
    steps,
    activeStep,
    onStepClick,
    onContinue,
    onBack,
    onStepSelect,
    orientation = "horizontal",
    stepContentText,
    savedDisplayActive = 0,
    progressStarted = false,
    actionsDisabled,
    continueLabel = "MARK COMPLETE",
    backLabel = "MARK IN-PROGRESS",
  } = props;

  const vertical = orientation === "vertical";
  const guided = vertical && onContinue && onBack;

  if (guided) {
    const stepperActiveStep = Math.min(activeStep, steps.length - 1);
    const expandedIndex =
      activeStep >= steps.length ? -1 : activeStep;

    return (
      <Stepper
        nonLinear
        activeStep={stepperActiveStep}
        orientation="vertical"
        sx={verticalStepperSx}
      >
        {steps.map((label, idx) => {
          const stageCompleted = idx < savedDisplayActive;
          const allComplete = savedDisplayActive >= steps.length;
          const isCurrentProgressStep =
            progressStarted && idx === savedDisplayActive && savedDisplayActive < steps.length;
          const canMarkComplete = !stageCompleted && !allComplete;
          const canMarkInProgress = !isCurrentProgressStep;

          return (
            <Step key={idx} completed={stageCompleted} expanded={expandedIndex === idx}>
              <StepButton
                color="inherit"
                onClick={() => onStepSelect?.(idx)}
                sx={{ justifyContent: "flex-start", py: 1, alignItems: "flex-start" }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    textTransform: "capitalize",
                    textAlign: "left",
                  }}
                >
                  <Typography variant="h4" component="span">
                    {label}
                  </Typography>
                  {isCurrentProgressStep && (
                    <Chip
                      size="small"
                      label={PROGRESS_LANES.inProgress.label}
                      sx={inProgressChipSx}
                    />
                  )}
                </Box>
              </StepButton>
              <StepContent>
                {stepContentText && expandedIndex === idx && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2, maxWidth: 480 }}>
                    {stepContentText}
                  </Typography>
                )}
                {expandedIndex === idx && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pb: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={actionsDisabled || !canMarkComplete}
                      onClick={onContinue}
                    >
                      {continueLabel}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={actionsDisabled || !canMarkInProgress}
                      onClick={onBack}
                    >
                      {backLabel}
                    </Button>
                  </Box>
                )}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    );
  }

  return (
    <Stepper
      alternativeLabel={!vertical}
      orientation={vertical ? "vertical" : "horizontal"}
      activeStep={activeStep}
      sx={vertical ? verticalStepperSx : stepperSx}
    >
      {steps.map((o, idx) => (
        <Step key={idx}>
          <StepButton
            color="#000"
            disabled={!onStepClick}
            onClick={() => onStepClick && onStepClick(idx)}
          >
            {o}
          </StepButton>
        </Step>
      ))}
    </Stepper>
  );
}
